import { bridge, delay, isNil, isAndroid, localStorageBridge } from '../../utils';
import { useBottomSheetRoute } from '../useBottomSheetRoute';
import { useIsInFragmentScreens } from '../useIsInFragmentScreens';
import { useCreateLikeFolderBottomSheet } from './hooks/useCreateLikeFolderBottomSheet';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { likeGoodsService } from '../../api';
import { useCreateNewLikeFolderBottomSheet } from './hooks/useCreateNewLikeFolderBottomSheet';
import { systemAlert } from '../../component';
import { toast } from './toast';
import { queryKey } from '../../constant';
import type { GetMemberInfoResponse } from '../../api/getMemberInfo/types';
import { useShowAnonymousLikeCountLimitAlert } from './hooks/useShowAnonymousLikeCountLimitAlert';
import { useMyInfo } from '../../queries';
import type { LogAnalyticsEventParams } from '../../utils';
import { useCallback } from 'react';
import { useSafeArea } from '@banhala/ui-kit';
import { useOpenDeeplink } from '../useOpenDeeplink';
import { useSkipFolderSelection } from '../useSkipFolderSelection';
import { useBottomDockedHeight } from '../useBottomDockedHeight';
import { useLaboratorySetting } from '../useLaboratorySetting';

const DEFAULT_FOLDER_NAME = '기본 서랍';
const DEFAULT_FOLDER_SNO = 0;
const STORAGE_KEY = 'isShownCreateLikeFolder';

const ANONYMOUS_USER_LIKE_COUNT = 5;

type UseAddLikeGoodsProps = {
  listParams?: string;
  onChangeIsLiked: (sno: number, nextValue: boolean) => void;
  screen: string;
  hideToast?: boolean;
};

export const useAddLikeGoods = ({
  onChangeIsLiked,
  listParams,
  screen,
  hideToast,
}: UseAddLikeGoodsProps) => {
  const skipFolderSelection = useSkipFolderSelection();
  const { saveGoodsToDefaultLikeFolder } = useSaveGoodsToDefaultLikeFolder(
    onChangeIsLiked,
    screen,
    listParams,
    hideToast,
  );
  const { show: showAnonymousLikeCountLimitAlert } =
    useShowAnonymousLikeCountLimitAlert();
  // ENABLE-341: atom 비동기 로드 race로 첫 진입 시 undefined → false fallback 되던 이슈.
  // 매 호출마다 loadValue로 직접 await하여 atom 미로딩 시점에도 정상 분기.
  const { loadValue: loadIsAlwaysShowLikeFolder } =
    useLaboratorySetting('IS_LIKE_FOLDER');

  const { data: originMemberInfo, refetch } = useMyInfo();

  const { show: createNewLikeFolderBottomSheet } =
    useCreateNewLikeFolderBottomSheet();
  const { show: showInlineLikeFolderSheet } = useCreateLikeFolderBottomSheet();
  const { open: openBottomSheetRoute } = useBottomSheetRoute();
  const isInFragmentScreens = useIsInFragmentScreens();
  const { openDeeplink: openDeeplinkForToast } = useOpenDeeplink();
  // 서랍 선택 후 toast 발화 시 시스템 navigation bar / 도킹 영역에 가리지 않도록
  // 충분한 bottomOffset 확보. useSaveGoodsToDefaultLikeFolder와 동일 패턴.
  const { bottom: safeAreaBottom } = useSafeArea();
  const bottomDockedHeight = useBottomDockedHeight();

  const getHandleCondition = useCallback(async () => {
    const getMemberInfo = async () => {
      if (!originMemberInfo) {
        const reFetchedMemberInfo = await refetch().then(result => result.data);

        return reFetchedMemberInfo ?? null;
      }

      if (originMemberInfo.is_anonymous) {
        const reFetchedMemberInfo = await refetch().then(result => result.data);

        return reFetchedMemberInfo ?? originMemberInfo;
      }
      return originMemberInfo;
    };

    const memberInfo = await getMemberInfo();
    if (!memberInfo) return;

    const { like_folders_count, is_anonymous } = memberInfo;
    const isChecked = await (localStorageBridge.getBoolean(STORAGE_KEY) ??
      false);

    if (is_anonymous) {
      if (memberInfo.likes_count >= ANONYMOUS_USER_LIKE_COUNT) {
        return 'blockAnonymouseLike';
      }
      if (memberInfo.likes_count >= ANONYMOUS_USER_LIKE_COUNT - 1) {
        // 비회원이 찜 5개 찼을 때
        return 'anonymousLikeLimit';
      }
      return 'justSaveDefaultLikeFolder';
    }

    if (isChecked === false && like_folders_count < 2) {
      return 'showTutorialAlert';
    }
    if (like_folders_count > 1) {
      const isAlwaysShowLikeFolder = await loadIsAlwaysShowLikeFolder();
      return isAlwaysShowLikeFolder
        ? 'showCreateNewLikeFolder'
        : 'justSaveDefaultLikeFolder';
    }
    if (like_folders_count === 1) {
      return 'justSaveDefaultLikeFolder';
    }
  }, [loadIsAlwaysShowLikeFolder, originMemberInfo, refetch]);

  const selectFolder = useCallback<
    (sno: number) => Promise<{ folderName: string; folderSno: number } | null>
  >(
    async (sno: number) => {
      // FragmentScreens 위에 있는 경우 native 탭바를 덮기 위해 별도 modal surface로 띄움
      // 그 외에는 기존 in-tree 바텀시트로 띄움
      const result = isInFragmentScreens
        ? await openBottomSheetRoute<
            {
              goodsSno: number;
              onChangeIsLiked: (sno: number, nextValue: boolean) => void;
              hideToast?: boolean;
              screen: string;
            },
            {
              showCreateNewLikeFolder: boolean;
              folder?: { sno: number; name: string };
            }
          >('folder-picker', {
            goodsSno: sno,
            onChangeIsLiked,
            hideToast,
            screen,
          })
        : await showInlineLikeFolderSheet({
            goodsSno: sno,
            onChangeIsLiked,
            hideToast,
            screen,
          });
      if (result.type === 'cancel') {
        return null;
      }
      if (result.data.showCreateNewLikeFolder) {
        // iOS는 이전 modal이 dismiss 중일 때 새 modal present를 무시함.
        // FragmentScreens 경로(모달 surface)에서 시트→시트로 체이닝할 때 짧은 delay로 race 회피.
        if (isInFragmentScreens) {
          await delay(10);
        }

        // Android: 첫 BottomSheet dismiss 애니메이션 종료를 대기해야 다음 모달이 silently drop되지 않음
        if (isAndroid) {
          await delay(300);
        }

        const result = isInFragmentScreens
          ? await openBottomSheetRoute<
              {
                goodsSno: number;
                listParams?: string;
                screen: string;
              },
              {
                goodsSno: number;
                folderName: string;
                folderSno: number;
              }
            >('create-new-folder', {
              goodsSno: sno,
              listParams,
              screen,
            })
          : await createNewLikeFolderBottomSheet({
              goodsSno: sno,
              listParams: listParams,
              screen,
            });
        if (result.type === 'confirm') {
          onChangeIsLiked(result.data.goodsSno, true);
          // 새 서랍 만들기 후 toast 발화 — 모달이 dismiss된 직후(=createNewLikeFolderBottomSheet resolve 후) 발화.
          // 기존 서랍 선택 경로와 동일 패턴으로 통일 (모달 내부 호출 시 Android dismiss race로 묻힘 + bottomOffset 누락 회피).
          const { folderName, folderSno } = result.data;
          if (!hideToast) {
            toast({
              folderName,
              folderSno,
              screen,
              // Android 시스템 navigation bar / 도킹 영역에 가리지 않도록 충분한 마진 확보
              bottomOffset: safeAreaBottom + bottomDockedHeight + 12,
              loggingAnalytics: { GOODS_SNO: sno },
              openDeeplink: openDeeplinkForToast,
            });
          }
          return {
            folderName,
            folderSno,
          };
        }
      } else {
        // 기존 서랍 선택 — 모달이 dismiss된 직후(=show resolve 후) toast 발화.
        // 모달 내부에서 toast를 띄우면 Android는 dismiss 애니메이션 race로 묻히므로 호출자에서 처리.
        const folderName = result.data.folder!.name;
        const folderSno = result.data.folder!.sno;
        if (!hideToast) {
          toast({
            folderName,
            folderSno,
            screen,
            // Android 시스템 navigation bar / 도킹 영역에 가리지 않도록 충분한 마진 확보
            bottomOffset: safeAreaBottom + bottomDockedHeight + 12,
            loggingAnalytics: { GOODS_SNO: sno },
            openDeeplink: openDeeplinkForToast,
          });
        }
        return { folderName, folderSno };
      }
      // unreachable
      return null;
    },
    [
      createNewLikeFolderBottomSheet,
      isInFragmentScreens,
      listParams,
      onChangeIsLiked,
      openBottomSheetRoute,
      screen,
      showInlineLikeFolderSheet,
      hideToast,
      openDeeplinkForToast,
      safeAreaBottom,
      bottomDockedHeight,
    ],
  );

  const showTutorialAlert = useCallback(
    (sno: number) => {
      return new Promise<{ folderName: string; folderSno: number } | null>(
        resolve => {
          systemAlert(
            '서랍 만들기',
            '서랍은 찜을 담아두는 새로운 공간이에요. 찜한 상품을 새로운 서랍에 넣어볼까요?',
            [
              {
                text: '다음에요',
                onPress: () => {
                  localStorageBridge.putBoolean(STORAGE_KEY, true);
                  saveGoodsToDefaultLikeFolder(sno);
                  resolve({
                    folderName: DEFAULT_FOLDER_NAME,
                    folderSno: DEFAULT_FOLDER_SNO,
                  });
                },
              },
              {
                text: '네',
                onPress: async () => {
                  localStorageBridge.putBoolean(STORAGE_KEY, true);
                  const result = await selectFolder(sno);
                  if (result) {
                    resolve(result);
                  }
                },
              },
            ],
          );
        },
      );
    },
    [saveGoodsToDefaultLikeFolder, selectFolder],
  );

  /**
   * - 조건에 따라 알럿 또는 바텀시트가 나온다.
   */
  const addLikeGoods = useCallback<
    (
      sno: number,
      analyticsParams?: LogAnalyticsEventParams['analytics'],
      ablyOnlyLog?: LogAnalyticsEventParams['ably_only_log'],
    ) => Promise<{
      folderName: string;
      folderSno: number;
    } | null>
  >(
    async (sno, analyticsParams, ablyOnlyLog) => {
      const handleCondition = await getHandleCondition();

      if (isNil(handleCondition)) {
        return null;
      }

      switch (handleCondition) {
        case 'blockAnonymouseLike':
        case 'anonymousLikeLimit': {
          if (handleCondition === 'anonymousLikeLimit') {
            await saveGoodsToDefaultLikeFolder(sno);
          }
          bridge.logAnalyticsEvent({
            name: 'VIEW_POPUP_LIKE_GOODS_LIMIT',
            analytics: analyticsParams,
            ably_only_log: ablyOnlyLog,
          });

          showAnonymousLikeCountLimitAlert().then(res => {
            if (res.type === 'confirm') {
              bridge.logAnalyticsEvent({
                name: 'CLICK_LIKE_POPUP_SIGN_UP',
                analytics: analyticsParams,
                ably_only_log: ablyOnlyLog,
              });
              bridge.assertLogin({});
            }
          });
          return {
            folderName: DEFAULT_FOLDER_NAME,
            folderSno: DEFAULT_FOLDER_SNO,
          };
        }
        case 'showTutorialAlert':
        case 'showCreateNewLikeFolder': {
          if (skipFolderSelection) {
            saveGoodsToDefaultLikeFolder(sno);
            return {
              folderName: DEFAULT_FOLDER_NAME,
              folderSno: DEFAULT_FOLDER_SNO,
            };
          }
          return handleCondition === 'showTutorialAlert'
            ? await showTutorialAlert(sno)
            : await selectFolder(sno);
        }
        case 'justSaveDefaultLikeFolder': {
          saveGoodsToDefaultLikeFolder(sno);
          return {
            folderName: DEFAULT_FOLDER_NAME,
            folderSno: DEFAULT_FOLDER_SNO,
          };
        }
        default:
          return null;
      }
    },
    [
      getHandleCondition,
      showAnonymousLikeCountLimitAlert,
      showTutorialAlert,
      selectFolder,
      saveGoodsToDefaultLikeFolder,
      skipFolderSelection,
    ],
  );

  return {
    addLikeGoods,
  };
};
const useSaveGoodsToDefaultLikeFolder = (
  onChangeIsLiked: (sno: number, nextValue: boolean) => void,
  screen: string,
  listParams?: string,
  hideToast?: boolean,
) => {
  const client = useQueryClient();
  const { bottom } = useSafeArea();
  const { openDeeplink } = useOpenDeeplink();
  const bottomDockedHeight = useBottomDockedHeight();

  const { mutate: mutateLikeGoods } = useMutation({
    mutationFn: likeGoodsService.post,
    throwOnError: false,
    onSuccess: async ({ data }, { folder_sno, sno }) => {
      onChangeIsLiked(sno, true);

      if (!hideToast) {
        toast({
          folderName: '기본 서랍',
          folderSno: folder_sno,
          screen,
          bottomOffset: bottom + bottomDockedHeight + 12,
          loggingAnalytics: { GOODS_SNO: sno },
          openDeeplink,
          snackbar: data.snackbar,
        });
      }

      // queryKey.myInfo 갱신하기 (useMyInfo가 구독하는 캐시)

      client.setQueryData<GetMemberInfoResponse>(
        queryKey.myInfo,
        oldData => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            likes_count: data.likes_count,
          };
        },
      );

      bridge.likeGoods({
        goods_sno: sno,
        is_liked: true,
        folder_sno,
      });
    },
  });

  const saveGoodsToDefaultLikeFolder = async (sno: number) => {
    try {
      mutateLikeGoods({
        list_params_referrer: listParams,
        sno,
        folder_sno: 0,
      });
    } catch (ex) {
      const error = ex as Error;

      bridge.toast(error.message);
    }
  };
  return { saveGoodsToDefaultLikeFolder };
};
